import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { useTaxYear } from "../contexts/TaxYearContext";
import {
  Lock, Loader2, CheckCircle2, AlertTriangle, XCircle, ArrowRight, Calendar,
  Package, FileText, Receipt, ShieldCheck, Archive as ArchiveIcon
} from "lucide-react";
import { toast } from "sonner";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

// The reports that get auto-archived in Screen 2. Mirrors the list documented in
// /app/memory/PRD.md under Gate 3d Batch 2. Each entry maps to a /api/archive POST
// payload — the backend re-renders the PDF and stores the bytes immutably.
const ARCHIVE_TARGETS = (taxYear, fy) => [
  { category: "Tax Prep", title: `Tax Prep Document — Tax Year ${taxYear}`, source: "/api/reports/turbotax/pdf", source_params: { tax_year: taxYear } },
  { category: "Income", title: `Income Snapshot — Tax Year ${taxYear}`, source: "/api/reports/income-pdf", source_params: { tax_year: taxYear } },
  { category: "Expenses", title: `Expenses Snapshot — Tax Year ${taxYear}`, source: "/api/reports/expenses-pdf", source_params: { tax_year: taxYear } },
  { category: "Grants", title: `Grant Tracking Snapshot — Tax Year ${taxYear}`, source: "/api/reports/grant-pdf", source_params: { tax_year: taxYear } },
  { category: "Equipment", title: `Equipment & Assets Snapshot — Tax Year ${taxYear}`, source: "/api/reports/equipment-pdf", source_params: { tax_year: taxYear } },
  { category: "Form 4562", title: `Form 4562 Part I (§179) — Tax Year ${taxYear}`, source: "/api/equipment/form-4562-pdf", source_params: { tax_year: taxYear } },
  { category: "Notes", title: `Notes Snapshot — Tax Year ${taxYear}`, source: "/api/notes/pdf", source_params: {} },
  { category: "CACFP Allocation", title: `CACFP Allocation — FY ${fy}`, source: "/api/cacfp/allocation/pdf", source_params: { fiscal_year: fy } },
  { category: "CACFP Use Charge", title: `CACFP Use Charge — FY ${fy}`, source: "/api/cacfp/use-charge/pdf", source_params: { fiscal_year: fy } },
  { category: "CACFP Ledger", title: `CACFP Monthly Ledger — FY ${fy}`, source: "/api/cacfp/ledger/pdf", source_params: { fiscal_year: fy } },
  { category: "CACFP Food & Labor", title: `CACFP Food & Labor — FY ${fy}`, source: "/api/cacfp/food-labor/pdf", source_params: { fiscal_year: fy } },
];

export default function YearEnd() {
  const navigate = useNavigate();
  const { taxYear } = useTaxYear();
  const [step, setStep] = useState(1); // 1=preflight, 2=backup, 3=confirm
  const [preflight, setPreflight] = useState(null);
  const [loading, setLoading] = useState(true);
  const [entitlement, setEntitlement] = useState(null);
  const [archiving, setArchiving] = useState(false);
  const [archiveResults, setArchiveResults] = useState([]); // [{target, status, id|error}]
  const [archiveIds, setArchiveIds] = useState([]);
  const [acknowledgedAmbers, setAcknowledgedAmbers] = useState({});
  const [confirmCheck, setConfirmCheck] = useState(false);
  const [closing, setClosing] = useState(false);
  const [closedAt, setClosedAt] = useState(null);

  const loadPreflight = useCallback(async () => {
    setLoading(true);
    try {
      const [pf, ent] = await Promise.all([
        axios.get(`${API}/year-end/preflight?tax_year=${taxYear}`, { withCredentials: true }),
        axios.get(`${API}/tax-year-entitlements/${taxYear}`, { withCredentials: true }).catch(() => ({ data: null })),
      ]);
      setPreflight(pf.data);
      setEntitlement(ent.data);
    } catch (err) {
      toast.error("Failed to load Year-End preflight");
    } finally {
      setLoading(false);
    }
  }, [taxYear]);

  useEffect(() => { loadPreflight(); }, [loadPreflight]);

  const fiscalYear = taxYear; // assumes calendar mode — for usda_fy users, the user-end UI shows both; close uses tax year primarily

  const runBackup = async () => {
    setArchiving(true);
    setArchiveResults([]);
    const ids = [];
    const targets = ARCHIVE_TARGETS(taxYear, fiscalYear);
    for (const tgt of targets) {
      try {
        const { data } = await axios.post(`${API}/archive`, {
          tax_year: taxYear,
          category: tgt.category,
          title: tgt.title,
          source: tgt.source,
          source_params: tgt.source_params,
          optional_note: `Auto-archived by Year-End wizard on ${new Date().toLocaleString()}`,
        }, { withCredentials: true });
        const newId = data?.id;
        if (newId) ids.push(newId);
        setArchiveResults((r) => [...r, { target: tgt, status: "ok", id: newId }]);
      } catch (err) {
        const msg = err?.response?.data?.detail || err.message;
        // Some archive targets may legitimately have no data (e.g., no Form 4562 if no
        // §179 elected). We treat 4xx errors as "skipped" rather than hard failures.
        const isSkip = err?.response?.status === 400 || err?.response?.status === 404;
        setArchiveResults((r) => [...r, { target: tgt, status: isSkip ? "skip" : "error", error: msg }]);
      }
    }
    setArchiveIds(ids);
    setArchiving(false);
    if (ids.length === 0) {
      toast.error("No reports could be archived. Check that you have data for this year.");
    } else {
      toast.success(`Archived ${ids.length} report${ids.length === 1 ? "" : "s"}`);
    }
  };

  const doClose = async () => {
    if (!confirmCheck) {
      toast.error("Please confirm you understand the lock.");
      return;
    }
    setClosing(true);
    try {
      const { data } = await axios.post(
        `${API}/year-end/close?tax_year=${taxYear}`,
        { archive_snapshot_ids: archiveIds },
        { withCredentials: true }
      );
      setClosedAt(data?.closed_at || new Date().toISOString());
      window.dispatchEvent(new CustomEvent("year-end:closed", { detail: { tax_year: taxYear } }));
      toast.success(`Tax Year ${taxYear} closed successfully.`);
    } catch (err) {
      toast.error(err?.response?.data?.detail?.message || "Failed to close year");
    } finally {
      setClosing(false);
    }
  };

  if (loading) {
    return (
      <div className="p-12 flex items-center justify-center text-slate-500">
        <Loader2 className="w-8 h-8 animate-spin mr-3" /> Loading Year-End check…
      </div>
    );
  }

  // Closed-already short-circuit
  if (entitlement?.entitlement_state === "read_only" || entitlement?.entitlement_state === "archived") {
    return (
      <div className="p-8 max-w-3xl mx-auto" data-testid="year-end-already-closed">
        <div className="bg-emerald-50 border-2 border-emerald-300 rounded-xl p-8 text-center">
          <Lock className="w-12 h-12 mx-auto text-emerald-700 mb-3" />
          <h1 className="text-3xl font-bold text-emerald-900">Tax Year {taxYear} is closed</h1>
          <p className="text-emerald-700 mt-2">
            Closed {entitlement.closed_at ? new Date(entitlement.closed_at).toLocaleString() : ""}.
            Your archived PDFs are safe in the Archive page forever.
          </p>
          <div className="mt-6 flex gap-3 justify-center">
            <button onClick={() => navigate("/app/archive")} className="px-5 py-2 bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg font-medium" data-testid="goto-archive-btn">
              <ArchiveIcon className="inline w-4 h-4 mr-2" /> Open Archive
            </button>
            <button onClick={() => navigate("/app/settings?tab=year-end")} className="px-5 py-2 bg-white border border-emerald-300 hover:bg-emerald-50 text-emerald-700 rounded-lg font-medium">
              Year-End Management
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (entitlement?.entitlement_state === "inactive") {
    return (
      <div className="p-8 max-w-3xl mx-auto" data-testid="year-end-inactive-warning">
        <div className="bg-amber-50 border-2 border-amber-300 rounded-xl p-8 text-center">
          <Calendar className="w-12 h-12 mx-auto text-amber-700 mb-3" />
          <h1 className="text-2xl font-bold text-amber-900">Tax Year {taxYear} is not active</h1>
          <p className="text-amber-700 mt-2">Activate this workspace first before you can close it.</p>
          <button onClick={() => navigate(`/app/year-end/activate?tax_year=${taxYear}`)} className="mt-6 px-5 py-2 bg-amber-600 hover:bg-amber-700 text-white rounded-lg font-medium" data-testid="goto-activate-btn">
            Activate Tax Year {taxYear} <ArrowRight className="inline w-4 h-4 ml-2" />
          </button>
        </div>
      </div>
    );
  }

  // SCREEN 3: success
  if (closedAt) {
    return (
      <div className="p-8 max-w-3xl mx-auto" data-testid="year-end-success">
        <div className="bg-emerald-50 border-2 border-emerald-300 rounded-xl p-10 text-center">
          <Lock className="w-16 h-16 mx-auto text-emerald-700 mb-4" />
          <h1 className="text-4xl font-bold text-emerald-900">Tax Year {taxYear} closed</h1>
          <p className="text-emerald-700 mt-3 text-lg">
            Closed on {new Date(closedAt).toLocaleString()}
          </p>
          <p className="text-emerald-700 mt-1">
            {archiveIds.length} archived PDF{archiveIds.length === 1 ? "" : "s"} safely stored in your Archive.
          </p>
          <div className="mt-8 flex flex-wrap gap-3 justify-center">
            <button onClick={() => navigate("/app/archive")} className="px-6 py-3 bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg font-medium" data-testid="success-open-archive">
              <ArchiveIcon className="inline w-4 h-4 mr-2" /> Open Archive
            </button>
            <button onClick={() => navigate("/app/dashboard")} className="px-6 py-3 bg-white border border-emerald-300 hover:bg-emerald-50 text-emerald-700 rounded-lg font-medium">
              Back to Dashboard
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 lg:p-8 max-w-5xl mx-auto space-y-6" data-testid="year-end-wizard">
      {/* Header */}
      <div className="flex items-start justify-between">
        <div>
          <h1 className="text-3xl font-bold text-slate-900">
            <Lock className="inline w-7 h-7 mr-2 text-indigo-600" /> Year-End — Tax Year {taxYear}
          </h1>
          <p className="text-slate-600 mt-1">Close out the year so your accountant has a permanent, immutable snapshot.</p>
        </div>
        <div className="flex items-center gap-2 text-sm text-slate-500" data-testid="year-end-step-indicator">
          <span className={`px-3 py-1 rounded-full ${step >= 1 ? "bg-indigo-100 text-indigo-700" : "bg-slate-100"}`}>1. Check</span>
          <ArrowRight className="w-4 h-4" />
          <span className={`px-3 py-1 rounded-full ${step >= 2 ? "bg-indigo-100 text-indigo-700" : "bg-slate-100"}`}>2. Backup</span>
          <ArrowRight className="w-4 h-4" />
          <span className={`px-3 py-1 rounded-full ${step >= 3 ? "bg-indigo-100 text-indigo-700" : "bg-slate-100"}`}>3. Lock</span>
        </div>
      </div>

      {/* SCREEN 1 — preflight */}
      {step === 1 && preflight && (
        <div className="space-y-4">
          {/* Red blocks */}
          {preflight.red_blocks.length > 0 && (
            <div className="bg-red-50 border border-red-300 rounded-xl p-5" data-testid="year-end-red-blocks">
              <div className="flex items-start gap-2 mb-3">
                <XCircle className="w-5 h-5 text-red-700 flex-shrink-0 mt-0.5" />
                <div>
                  <h3 className="font-semibold text-red-900">Must fix before closing</h3>
                  <p className="text-sm text-red-700">Resolve these {preflight.red_blocks.length} item{preflight.red_blocks.length === 1 ? "" : "s"} first.</p>
                </div>
              </div>
              <ul className="space-y-2">
                {preflight.red_blocks.map((r) => (
                  <li key={r.code} className="flex items-center justify-between bg-white border border-red-200 rounded p-3" data-testid={`red-block-${r.code}`}>
                    <span className="text-sm text-red-900">{r.message}</span>
                    {r.action_url && (
                      <button onClick={() => navigate(r.action_url)} className="text-xs px-3 py-1 bg-red-600 hover:bg-red-700 text-white rounded">
                        Review →
                      </button>
                    )}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Amber warnings */}
          {preflight.amber_warnings.length > 0 && (
            <div className="bg-amber-50 border border-amber-300 rounded-xl p-5" data-testid="year-end-amber-warnings">
              <div className="flex items-start gap-2 mb-3">
                <AlertTriangle className="w-5 h-5 text-amber-700 flex-shrink-0 mt-0.5" />
                <div>
                  <h3 className="font-semibold text-amber-900">Warnings to acknowledge</h3>
                </div>
              </div>
              <ul className="space-y-2">
                {preflight.amber_warnings.map((w) => (
                  <li key={w.code} className="flex items-start gap-3 bg-white border border-amber-200 rounded p-3">
                    <input
                      type="checkbox"
                      id={`ack-${w.code}`}
                      checked={!!acknowledgedAmbers[w.code]}
                      onChange={(e) => setAcknowledgedAmbers({ ...acknowledgedAmbers, [w.code]: e.target.checked })}
                      className="mt-1"
                      data-testid={`amber-ack-${w.code}`}
                    />
                    <label htmlFor={`ack-${w.code}`} className="text-sm text-amber-900 cursor-pointer">{w.message}</label>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Green summary */}
          <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-5">
            <div className="flex items-center gap-2 mb-3">
              <CheckCircle2 className="w-5 h-5 text-emerald-700" />
              <h3 className="font-semibold text-emerald-900">Summary that will be locked</h3>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
              <div><Receipt className="inline w-4 h-4 mr-1 text-emerald-600" /><strong className="block text-emerald-900 text-lg">${preflight.green_summary.total_income.toLocaleString()}</strong>Total income</div>
              <div><FileText className="inline w-4 h-4 mr-1 text-emerald-600" /><strong className="block text-emerald-900 text-lg">${preflight.green_summary.total_expenses.toLocaleString()}</strong>Total expenses</div>
              <div><ShieldCheck className="inline w-4 h-4 mr-1 text-emerald-600" /><strong className="block text-emerald-900 text-lg">${preflight.green_summary.net_income.toLocaleString()}</strong>Net income</div>
              <div><Package className="inline w-4 h-4 mr-1 text-emerald-600" /><strong className="block text-emerald-900 text-lg">{preflight.green_summary.equipment_count_confirmed}</strong>Equipment items</div>
            </div>
          </div>

          {/* Action */}
          <div className="flex items-center justify-between pt-2">
            <button onClick={loadPreflight} className="text-sm text-slate-600 hover:text-slate-900">↻ Re-check</button>
            <button
              data-testid="continue-to-backup-btn"
              disabled={!preflight.can_proceed || preflight.amber_warnings.some((w) => !acknowledgedAmbers[w.code])}
              onClick={() => setStep(2)}
              className="px-6 py-2.5 bg-indigo-600 hover:bg-indigo-700 disabled:bg-slate-300 disabled:cursor-not-allowed text-white rounded-lg font-medium"
            >
              Continue to Backup <ArrowRight className="inline w-4 h-4 ml-2" />
            </button>
          </div>
        </div>
      )}

      {/* SCREEN 2 — backup */}
      {step === 2 && (
        <div className="space-y-4" data-testid="year-end-backup-screen">
          <div className="bg-indigo-50 border border-indigo-200 rounded-xl p-5">
            <h3 className="font-semibold text-indigo-900 mb-2">Backing up your year</h3>
            <p className="text-sm text-indigo-700 mb-3">We'll generate and immutably archive a PDF of every report so your accountant can reference them anytime — even if you don't renew next year.</p>
            {!archiving && archiveResults.length === 0 && (
              <button data-testid="start-backup-btn" onClick={runBackup} className="px-5 py-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg font-medium">
                Start Backup
              </button>
            )}
          </div>

          {archiveResults.length > 0 && (
            <div className="bg-white border border-slate-200 rounded-xl divide-y">
              {archiveResults.map((r, i) => (
                <div key={i} className="flex items-center justify-between p-3 text-sm" data-testid={`archive-result-${r.target.category.replace(/\s/g, '-')}`}>
                  <span className="text-slate-700">{r.target.title}</span>
                  {r.status === "ok" && <CheckCircle2 className="w-4 h-4 text-emerald-600" />}
                  {r.status === "skip" && <span className="text-xs text-slate-400">Skipped (no data)</span>}
                  {r.status === "error" && <span className="text-xs text-red-600" title={r.error}>Failed</span>}
                </div>
              ))}
              {archiving && (
                <div className="flex items-center gap-2 p-3 text-sm text-slate-500">
                  <Loader2 className="w-4 h-4 animate-spin" /> Archiving…
                </div>
              )}
            </div>
          )}

          <div className="flex items-center justify-between pt-2">
            <button onClick={() => setStep(1)} className="text-sm text-slate-600 hover:text-slate-900">← Back</button>
            <button
              data-testid="continue-to-lock-btn"
              disabled={archiving || archiveIds.length === 0}
              onClick={() => setStep(3)}
              className="px-6 py-2.5 bg-indigo-600 hover:bg-indigo-700 disabled:bg-slate-300 disabled:cursor-not-allowed text-white rounded-lg font-medium"
            >
              Continue to Lock <ArrowRight className="inline w-4 h-4 ml-2" />
            </button>
          </div>
        </div>
      )}

      {/* SCREEN 3 — confirm */}
      {step === 3 && (
        <div className="space-y-4" data-testid="year-end-confirm-screen">
          <div className="bg-red-50 border-2 border-red-300 rounded-xl p-6">
            <div className="flex items-start gap-3">
              <Lock className="w-6 h-6 text-red-700 flex-shrink-0 mt-1" />
              <div className="flex-1">
                <h3 className="text-lg font-semibold text-red-900">Confirm and lock Tax Year {taxYear}</h3>
                <p className="text-sm text-red-700 mt-1">After locking, you cannot edit any {taxYear} data without first unlocking it from Settings → Year-End Management.</p>
                <p className="text-sm text-red-700 mt-1">Your {archiveIds.length} archived PDF{archiveIds.length === 1 ? "" : "s"} will remain in your Archive forever.</p>
                <label className="flex items-start gap-2 mt-4 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={confirmCheck}
                    onChange={(e) => setConfirmCheck(e.target.checked)}
                    className="mt-1"
                    data-testid="confirm-lock-checkbox"
                  />
                  <span className="text-sm text-red-900">I understand that after locking, I cannot edit {taxYear} data without unlocking from Settings → Year-End Management.</span>
                </label>
              </div>
            </div>
          </div>
          <div className="flex items-center justify-between">
            <button onClick={() => setStep(2)} className="text-sm text-slate-600 hover:text-slate-900">← Back</button>
            <button
              data-testid="confirm-lock-btn"
              disabled={!confirmCheck || closing}
              onClick={doClose}
              className="px-6 py-2.5 bg-red-600 hover:bg-red-700 disabled:bg-slate-300 disabled:cursor-not-allowed text-white rounded-lg font-medium"
            >
              {closing ? <><Loader2 className="inline w-4 h-4 animate-spin mr-2" /> Locking…</> : <><Lock className="inline w-4 h-4 mr-2" /> Confirm and Lock {taxYear}</>}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
